import React from "react";
import {useTranslation} from "react-i18next";
import {FaIcon} from "../FaIcon";

export const CURRENCY_CONVERSION_VARIANT_BUSINESS_UNIT = 'business_unit';
export const CURRENCY_CONVERSION_VARIANT_ACCOUNT = 'account';

/**
 * Aviso de que los importes mostrados están convertidos desde la moneda de la cuenta
 * publicitaria a la de la línea de control.
 *
 * Es informativo, no un error: usa el ícono gris (`info-icon-container`), nunca el ámbar de
 * warning. Sin este aviso el usuario compara el tablero contra la plataforma, ve otro número
 * y lo reporta como bug (caso PML: cuenta en CLP, línea en ARS, todo x1,60).
 *
 * `conversions`: [{from, to, rate}] tal como las devuelve el backend. `rate` puede ser null
 * si no se pudo resolver el tipo de cambio — en ese caso se avisa igual, sin el valor.
 */
export function CurrencyConversionInfo({conversions, variant = CURRENCY_CONVERSION_VARIANT_ACCOUNT}) {
    const {t} = useTranslation();

    if (!Array.isArray(conversions) || conversions.length === 0) {
        return null;
    }

    const tooltipText = variant === CURRENCY_CONVERSION_VARIANT_BUSINESS_UNIT
        ? businessUnitTooltipText(conversions, t)
        : accountTooltipText(conversions[0], t);

    return (
        <div className="info-icon-container currency-conversion-info">
            <FaIcon iconName="info-circle"/>
            <div className="warning-tooltip -rich">
                <div className="warning-tooltip-body">
                    <p className="warning-tooltip-text">{tooltipText}</p>
                </div>
            </div>
        </div>
    );
}

function accountTooltipText(conversion, t) {
    return t('dashboard.currency_conversion.account_tooltip', {
        from: conversion.from,
        to: conversion.to,
        rate: rateText(conversion, t),
    });
}

function businessUnitTooltipText(conversions, t) {
    const rates = conversions.map((conversion) => rateText(conversion, t)).join(' · ');
    return t('dashboard.currency_conversion.business_unit_tooltip', {
        to: conversions[0].to,
        rates: rates,
    });
}

function rateText(conversion, t) {
    if (conversion.rate === null || conversion.rate === undefined) {
        return t('dashboard.currency_conversion.rate_unknown', {from: conversion.from, to: conversion.to});
    }
    return t('dashboard.currency_conversion.rate', {
        from: conversion.from,
        to: conversion.to,
        rate: formatRate(conversion.rate),
    });
}

function formatRate(rate) {
    try {
        return rate.toLocaleString('es-AR', {maximumFractionDigits: 4});
    } catch (e) {
        return String(rate);
    }
}
